/*
 * The socket call this package needs and does not own. A reaction is a live chat mutation, so it is
 * a WebSocket type and not an HTTP route (protocol.md R9) — and the socket exists only inside the
 * boot, which is why every chat action already arrives through `chatDeps()`.
 *
 * `ChatDeps` gains its field here rather than in deps.tsx: an interface is open, so a package
 * declares the calls it needs beside the code that needs them, exactly as `ChatRequests` and
 * `ChatEvent` widen core's request and event maps. The module binds it in `bind.ts`.
 *
 * It is optional and may be unbound. A reaction control whose call is missing is rendered disabled
 * rather than hidden: a chip that quietly does nothing is worse than a chip that says it cannot.
 */

import { chatDeps } from "../deps.tsx"

declare module "../deps.tsx" {
	interface ChatDeps {
		/** `chat.reaction.add` when `on`, `chat.reaction.remove` when not (protocol.md §2.5.4). */
		react?:
			((input: { messageId: number; emoji: string; on: boolean }) => Promise<void>) | undefined
	}
}

/** True while the boot has bound the reaction call; the controls follow it. */
export function canReact(): boolean {
	return chatDeps().react !== undefined
}

/**
 * Adds or takes back the signed-in person's reaction. The server answers with the whole summary
 * and publishes `chat.reaction.changed` to the conversation, which the reducer applies — so
 * nothing here writes the model, and the two browsers of a round trip see the same chip.
 *
 * A refusal or a lost socket therefore needs no rollback: the chips on screen are the server's own
 * last word, and one that never changed is the truthful picture of a reaction that did not land.
 */
export function toggleReaction(messageId: number, emoji: string, on: boolean): void {
	void chatDeps()
		.react?.({ messageId, emoji, on })
		.catch(() => undefined)
}
